import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestAdminOtp, adminLogin } from '../../api/admin';
import AdminSecureStorage from '../../utils/adminSecureStorage';

const AdminLogin = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState<'request-otp' | 'verify-otp'>('request-otp');
    const [formData, setFormData] = useState({
        usernameOrEmail: '',
        otp: ''
    });
    const [, setOtpSent] = useState(false);
    const [maskedEmail, setMaskedEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(0);

    // Check if already authenticated
    useEffect(() => {
        if (AdminSecureStorage.isAdminTokenValid()) {
            navigate('/admin/dashboard');
        }
    }, [navigate]);

    // Countdown timer for OTP resend
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (countdown > 0) {
            interval = setInterval(() => {
                setCountdown(countdown - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [countdown]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError(''); // Clear error when user types
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (step === 'request-otp') {
            await handleRequestOtp();
        } else {
            await handleVerifyOtp();
        }
    };

    const handleRequestOtp = async () => {
        if (!formData.usernameOrEmail.trim()) {
            setError('Please enter username or email');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await requestAdminOtp({ 
                usernameOrEmail: formData.usernameOrEmail.trim() 
            });
            
            if (response.success) {
                setMaskedEmail(response.data.email);
                setOtpSent(true);
                setStep('verify-otp');
                setCountdown(300); // 5 minutes countdown
            } else {
                setError(response.message || 'Failed to send OTP');
            }
        } catch (error: any) {
            console.error('OTP request error:', error);
            setError(error.response?.data?.error || 'Failed to send OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!formData.otp.trim()) {
            setError('Please enter the OTP code');
            return;
        }

        if (formData.otp.length !== 6) {
            setError('OTP must be 6 digits');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await adminLogin({
                usernameOrEmail: formData.usernameOrEmail,
                otp: formData.otp
            });
            
            if (response.success) {
                // Store token
                AdminSecureStorage.setAdminToken(response.data.token, 24);
                
                // Store admin info (optional - could be retrieved later)
                sessionStorage.setItem('admin_info', JSON.stringify(response.data.admin));
                
                // Redirect to admin dashboard
                navigate('/admin/dashboard');
            } else {
                setError(response.message || 'Login failed');
            }
        } catch (error: any) {
            console.error('Admin login error:', error);
            setError(error.response?.data?.error || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (countdown > 0) return;
        
        setLoading(true);
        setError('');

        try {
            const response = await requestAdminOtp({ 
                usernameOrEmail: formData.usernameOrEmail 
            });
            
            if (response.success) {
                setCountdown(300); // Reset countdown
                setError('');
                // Show success message briefly
                const originalError = error;
                setError('OTP sent successfully!');
                setTimeout(() => setError(originalError), 3000);
            } else {
                setError(response.message || 'Failed to resend OTP');
            }
        } catch (error: any) {
            console.error('OTP resend error:', error);
            setError(error.response?.data?.error || 'Failed to resend OTP');
        } finally {
            setLoading(false);
        }
    };

    const handleBackToUsernameStep = () => {
        setStep('request-otp');
        setOtpSent(false);
        setFormData({ ...formData, otp: '' });
        setCountdown(0);
        setError('');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-cyan-100 flex items-center justify-center p-4">
            <div className="max-w-md w-full space-y-8">
                <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
                    {/* Header */}
                    <div className="text-center">
                        <div className="mx-auto h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                            {step === 'request-otp' ? (
                                <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            ) : (
                                <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            )}
                        </div>
                        <h2 className="mt-4 text-3xl font-bold text-gray-900">
                            {step === 'request-otp' ? 'Admin Login' : 'Verify OTP'}
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            {step === 'request-otp' 
                                ? 'Enter your credentials to receive verification code'
                                : `OTP sent to ${maskedEmail}. Enter the code to continue.`
                            }
                        </p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Form */}
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        {step === 'request-otp' ? (
                            <div>
                                <label htmlFor="usernameOrEmail" className="block text-sm font-medium text-gray-700">
                                    Username or Email
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="usernameOrEmail"
                                        name="usernameOrEmail"
                                        type="text"
                                        required
                                        value={formData.usernameOrEmail}
                                        onChange={handleChange}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="Enter your username or email"
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0">
                                            <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm text-blue-700">
                                                Check your email for the 6-digit verification code. It expires in {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                                        Verification Code
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            id="otp"
                                            name="otp"
                                            type="text"
                                            required
                                            maxLength={6}
                                            value={formData.otp}
                                            onChange={handleChange}
                                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-center text-2xl tracking-widest font-mono"
                                            placeholder="000000"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-between items-center">
                                    <button
                                        type="button"
                                        onClick={handleBackToUsernameStep}
                                        className="text-sm text-indigo-600 hover:text-indigo-500"
                                        disabled={loading}
                                    >
                                        ← Back to username
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleResendOtp}
                                        disabled={loading || countdown > 0}
                                        className="text-sm text-indigo-600 hover:text-indigo-500 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    >
                                        {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
                                    </button>
                                </div>
                            </>
                        )}

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {loading ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Signing in...
                                    </div>
                                ) : (
                                    step === 'request-otp' ? 'Send Verification Code' : 'Verify & Sign In'
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Footer */}
                    <div className="text-center">
                        <p className="text-xs text-gray-500">
                            VivaClub Admin Panel - Authorized Access Only
                        </p>
                    </div>
                </div>

                {/* Back to main site */}
                <div className="text-center">
                    <button
                        onClick={() => navigate('/')}
                        className="text-sm text-indigo-600 hover:text-indigo-500"
                    >
                        ← Back to main site
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
